import api from '@ibkr/clientportal.common/src/api/am.js'
import { safeCall } from '@ibkr/clientportal.common/src/js/promise'
import { httpFailed } from '@ibkr/clientportal.common/src/js/http'
import Metrics from '@ibkr/clientportal.common/src/js/Metrics'

export const types = {
    SET_AM_SESSION_ID : 'SET_AM_SESSION_ID',
    SET_AM_PENDING_ITEMS : 'SET_AM_PENDING_ITEMS',
    SET_AM_NOTIFICATIONS : 'SET_AM_NOTIFICATIONS',
    SET_AM_USER : 'SET_AM_USER',
    SET_AM_MENU : 'SET_AM_MENU',
    SET_AM_PA_PICKER_DATA : 'SET_AM_PA_PICKER_DATA',
    SET_TRANSACTIONS: 'SET_TRANSACTIONS',
    SET_AM_MENU: 'SET_AM_MENU',
    SET_AM_PICKER_LOADED: 'SET_AM_PICKER_LOADED',
    SET_IA_ELIGIBILITY: 'SET_IA_ELIGIBILITY',
    SET_IA_PARTNERID: 'SET_IA_PARTNERID',
    SET_IA_PARTITION: 'SET_IA_PARTITION',
    SET_MOST_RELEVANT_ACCOUNT: 'SET_MOST_RELEVANT_ACCOUNT',
    SET_AM_CONFIGURATION: 'SET_AM_CONFIGURATION'
}

const state = {
    /**
     * All calls to AM services require the header to have sessionId, otherwise the call is rejected.
     * Inside AM context, the session id is set globally on window.AM_SESSION_ID by the Angular app for 
     * convenience. Outside AM context, we first need to call a login servlet which will give us the session id.
     */
    sessionId: '',
    pendingItems: {
        data: [],
        error: false // true if the request to get pending items failed
    },
    notifications: {
        data: {
            corporateActions: [],
            messages: []
        },
        error: false // true if the request to get notifications failed
    },
    user: { title: "" },
    recentTransactions: [],
    amMenu: {},
    amPickerLoaded: false, // true if am picker has been loaded
    iaEligibleAccounts: [],
    mostRelevantAcct: undefined,
    amConfiguration: {},
    iaPartnerId: undefined //--used for IA content
}

const getters = {

    sessionId(state) {
        return state.sessionId;
    },
    pendingItemNum(state, getters) {
        return getters.amPendingItems.length;
    },
    messageNum(state, getters) {
        return getters.amMessages.length;
    },
    amUser(state) {
        return state.user || {};
    },
    corporateActionNum(state, getters) {
        return getters.amCorporateActions.length;
    },
    getRecentTransactions(state) {
        return state.recentTransactions;
    },
    viewMoreTransactions(state) {
        if (state.recentTransactions && state.recentTransactions.manipulator) {
            return state.recentTransactions.manipulator.services.PAGINATION.totalPages > 1;
        }
        return false;
    },
    amMenu(state) {
        return state.amMenu || {};
    },
    amPickerLoaded(state) {
        return state.amPickerLoaded;
    },
    amMessages(state) {
        return ((state.notifications?.data?.messages) || []).map(msg => {
            return {
                ...msg,
                ...{
                    epoch: msg.date ? $date.local(msg.date).unix() : undefined,
                    AM_MESSAGE: true
                }
            };
        });
    },
    amCorporateActions(state) {
        return ((state.notifications?.data?.corporateActions) || []).map(msg => {
            return {
                ...msg,
                ...{
                    epoch: msg.date ? $date.local(msg.date).unix() : undefined,
                    AM_CORP_ACTION: true
                }
            };
        });
    },
    amPendingItems(state) {
        return ((state.pendingItems?.data) || []).map(msg => {
            return {
                ...msg,
                ...{
                    epoch: msg.date ? $date.local(msg.date).unix() : undefined,
                    PENDING_ITEM: true
                }
            };
        });
    },
    iaEligibileAccounts(state) {
        return state.iaEligibleAccounts;
    },
    /** iaEligibility is a flag used as an indicator to include ICICIWidget on landing page.
     * It is true if the user already has an account with iaPartition
     * or if he/she has any accounts eligible to create iaPartition
     */
    iaEligibility(state, getters) {
        return getters.iaPartitionedAccount !== '' || state.iaEligibleAccounts.length > 0;
    },
    mostRelevantAccount(state) {
        return state.mostRelevantAcct;
    },
    //-- if the user has any account with ia partition return that account id
    iaPartitionedAccount(state, getters, rootState, rootGetters) {
        return (rootGetters.pickerAccounts.find(acct => acct.iaPartition) || {}).accountId || '';
    },
    // returns services with value true whose API call has failed
    amErrors(state) {
        return {
            pendingItems: state.pendingItems?.error,
            notifications: state.notifications?.error
        };
    },

    iaPartnerId(state) {
        return state.iaPartnerId;
    },

    classicAmEligible() {
        return state.user?.classicAmEligible;
    }
}

const mutations = {

    [types.SET_TRANSACTIONS](state, txns) {
        state.recentTransactions = txns;
    },
    [types.SET_AM_SESSION_ID](state, id) {
        state.sessionId = id;
    },
    [types.SET_AM_PENDING_ITEMS](state, payload) {
        if (payload.error) {
            state.pendingItems.error = true;
        } else if (payload.data) {
            state.pendingItems.data = payload.data.pendingItems;
        }
    },
    [types.SET_AM_NOTIFICATIONS](state, payload) {
        if (payload.error) {
            state.notifications.error = true;
        } else if (payload.data) {
            state.notifications.data = payload.data;
        }
    },
    [types.SET_AM_USER](state, data) {
        state.user = data;
    },
    [types.SET_AM_MENU](state, data) {
        state.amMenu = data;
    },
    [types.SET_AM_PICKER_LOADED](state, data) {
        state.amPickerLoaded = data;
    },
    [types.SET_IA_ELIGIBILITY](state, accountId) {
        if (!state.iaEligibleAccounts.includes(accountId)) {
            state.iaEligibleAccounts.push(accountId);
        }
    },
    [types.SET_IA_PARTNERID](state, partnerId) {
        state.iaPartnerId = partnerId;
    },
    [types.SET_MOST_RELEVANT_ACCOUNT](state, acctId) {
        state.mostRelevantAcct = acctId;
    },
    [types.SET_AM_CONFIGURATION](state, data) {
        for (const prop in data) {
            Vue.set(state.amConfiguration, prop, data[prop].accessible);
        }
    }
}

const actions = {

    getBaseServices({ state, commit }) {
        api.getUser()
            .then((res) => {
                console.debug('IB_LANG', window.IB_LANG, res.language);
                /*if (window.IB_LANG && res.language && res.language != window.IB_LANG) {
                    const id = locales.find(l => l.code == res.language);
                    if (id) { 
                        // need to check otherwise we risk setting an IB_LANG that doesn't exist and app 
                        // woudln't load; index.lang.js
                        setLocale(res.language);
                    }
                }*/
                commit(types.SET_AM_USER, res);
            })
            .catch((res) => httpFailed('am-user', commit, null));
        api.getPendingItems()
            .then((res) => commit(types.SET_AM_PENDING_ITEMS, { data: res }))
            .catch((res) => {
                httpFailed('am-pending-items', commit, res);
                commit(types.SET_AM_PENDING_ITEMS, { error: true })
            });
        api.getNotifications()
            .then((res) => commit(types.SET_AM_NOTIFICATIONS, { data: res }))
            .catch((res) => {
                httpFailed('am-notifications', commit, res);
                commit(types.SET_AM_NOTIFICATIONS, { error: true })
            });
    },

    setSessionId({ state, commit, dispatch, rootGetters}, { id, data, onSuccess }) {
        api.setSessionId(id);
        commit(types.SET_AM_SESSION_ID, id);
        safeCall('am-login', onSuccess, data);
    },

    setMostRelevantAccount({ state, commit }, data) {
        if (data && data.mostRelevantAccount) {
            commit(types.SET_MOST_RELEVANT_ACCOUNT, data.mostRelevantAccount);
        }
    },

    login({ state, commit, dispatch, rootGetters }, { isFreeTrial, onSuccess, onError }) {
        if (window.AM_SESSION_ID) {
            dispatch('setSessionId', { id: window.AM_SESSION_ID, data: {}, onSuccess: onSuccess });
            return;
        }
        api.authenticate(isFreeTrial).then((res) => {
            const sessId = res && res.sessionId;
            Metrics.instance().register({
                'type': 'login',
                'source': 'AM',
                'value': !sessId,
                'sessionId': sessId
            });
            if (!sessId) {
                safeCall('am-login', onError, res);
            } else {
                dispatch('setSessionId', { id: sessId, data: res, onSuccess: onSuccess });
                dispatch('setMostRelevantAccount', res);
            }
        }).catch(res => {
            httpFailed('am/login', commit, res);
            safeCall('am/login', onError, res);
        });
    },

    getTransactions({ commit, dispatch, state }) {
        api.getTransactions(90).then((res) => {
            if (res.entries) {
                commit(types.SET_TRANSACTIONS, res.entries);
            } else {
                commit(types.SET_TRANSACTIONS, []);
            }
        });
    },

    setLanguage(args, { lang, onSuccess }) {
        api.setLanguage(lang)
            .then(res => safeCall('am-set-lang', onSuccess, res))
            .catch(res => httpFailed('am-set-lang', args.commit, res));
    },

    getAmMenu({ commit, dispatch, state }) {
        api.getMenu()
            .then(res => {
                commit(types.SET_AM_MENU, res);
            })
            .catch(res => httpFailed('am menu', null, res));
    },

    // marks am picker as loaded/not loaded
    amPickerLoaded({ commit, dispatch, state }, { status }) {
        commit(types.SET_AM_PICKER_LOADED, status);
    },

    getIaEligibleAccounts({ state, commit, rootGetters }) {
        const pickerAccounts = rootGetters.pickerAccounts;
        if (pickerAccounts.length > 0 ) {
            for( let i =0; i < pickerAccounts.length; i++) {
                let acctId = pickerAccounts[i].accountId;
                api.iaPartitionEligibility(acctId)
                    .then((res)=> {
                        if (res.partnerId) {
                            commit(types.SET_IA_PARTNERID, res.partnerId);
                        }
                        commit(types.SET_IA_ELIGIBILITY, acctId);
                    });
            }
        }
    },

    getAmConfigurations({ commit, dispatch, state }, { acctId, configs }) {
        api.amConfiguration(acctId, configs)
            .then((res) => {
                commit(types.SET_AM_CONFIGURATION, res.configs);
            });
    },

}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}